/* @flow */
import * as React from 'react';
import cx from 'classnames';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { capitalize } from 'lodash';

import {
  removeMembers,
  loadMembership,
  selectMembership,
  unselectMembership,
} from 'reducers/peopleTables';
import { shortDescriptionForChatEligibilityState } from 'constants/CopyConstants';
import { pushRouterToChatWithMember } from 'lib/ChatRouteHelper';
import { withRouter } from 'app/context/Router';
import Button from 'components/ButtonDeprecated';
import Buttons from 'components/Buttons';
import Checkbox from 'components/Checkbox';
import ClickEventTrigger from 'components/ClickEventTrigger';
import CurrentUser from 'auth/CurrentUser';
import Glyph from 'components/Glyph';
import Group from 'entities/Group';
import GroupMemberFamily from 'groups/components/GroupMemberFamily';
import GroupMembership from 'entities/GroupMembership';
import LayerTrigger from 'components/LayerTrigger';
import MediaObject from 'components/MediaObject';
import PhoneCallButton from 'phoneCalls/components/PhoneCallButton';
import StartChatButton from 'user/components/StartChatButton';
import Text from 'components/Text';
import Timestamp from 'components/Timestamp';
import ToastActions from 'actions/ToastActions';
import Tooltip from 'components/Tooltip';
import UserActionsMenuButton from 'user/components/UserActionsMenuButton';
import UserAvatar from 'users/components/UserAvatar';
import mapEntitiesToUserActionsMenuButtonUser from 'user/lib/mapEntitiesToUserActionsMenuButtonUser';

import type { LoadMembershipOptions } from 'reducers/peopleTables';

type Props = {
  isSelected: boolean,
  group: Group,
  adminConsoleAccess: ?boolean,
  hasSelection: boolean,
  membership: GroupMembership,
  selectMembership: (groupId: Backbone$Id, membership: GroupMembership) => any,
  unselectMembership: (groupId: Backbone$Id, membership: GroupMembership) => any,
  removeMembers: (groupId: Backbone$Id, userIds: Backbone$Id[]) => any,
  loadMembership: (
    groupId: Backbone$Id,
    userId: Backbone$Id,
    options: LoadMembershipOptions
  ) => any,
  familySummary: ?Object, // shallow compare for shouldComponentUpdate
  canViewFamily: boolean,
  router: *,
};

import './styles/PeopleTableRow.scss';

class PeopleTableRow extends React.Component<Props> {
  handleClick = () => {
    if (this.props.adminConsoleAccess) {
      const fn = this.props.isSelected
        ? this.props.unselectMembership
        : this.props.selectMembership;

      fn(this.props.group.id, this.props.membership);
    } else {
      pushRouterToChatWithMember(
        this.props.router,
        this.props.membership.getUser(),
        this.props.group,
        () =>
          ToastActions.showErrorToast('Oops! We weren’t able to load that chat. Please try again.')
      );
    }
  };
  stopPropagation = (e: any) => e.stopPropagation();

  handleUserUpdate = () => {
    this.props.loadMembership(this.props.group.id, this.props.membership.getUserId(), {
      update: true,
    });
  };
  handleUserRemoved = () => {
    this.props.removeMembers(this.props.group.id, [this.props.membership.getUserId()]);
  };
  showCopyToClass = () => {
    const { membership } = this.props;
    const subscriber = membership.getUser();

    return subscriber && subscriber.getCanBeCopiedToClass();
  };

  renderRoleCell = (organizationAffiliation: Object) => {
    if (organizationAffiliation) {
      return (
        <td>
          <Text>{organizationAffiliation.roles.map(role => capitalize(role)).join(', ')}</Text>
        </td>
      );
    } else if (this.props.group.getOrganization()) {
      /* This shouldn't really happen but it might. The case is when a class
       * is part of an org, but for some reason the user is not part of that
       * org. This at least allows the table to render correctly */
      return <td />;
    }

    return null;
  };

  isCurrentUser = () => {
    return this.props.membership.getUser().id === CurrentUser.getUser().id;
  };

  render() {
    const { membership, group, adminConsoleAccess } = this.props;
    const subscriber = membership.getUser();
    const canEditGroup = group.canEdit();
    const canChatWithUser =
      membership.getCanChat() || subscriber.get('chat_eligibility_state') === 'eligible';
    const rowIsEnabled = adminConsoleAccess || (canEditGroup && canChatWithUser);

    const classes = cx('PeopleTableRow', {
      'PeopleTableRow--enabled': rowIsEnabled,
      'PeopleTableRow--selected': this.props.isSelected,
      'PeopleTableRow--hasSelection': this.props.hasSelection,
    });

    const organizationAffiliation = membership.getOrgAffiliationView();
    const canPhone = CurrentUser.getUser().hasFeature('phone_calls');
    const chatColor = canChatWithUser ? 'mascot' : 'graphite';
    const note = this.isCurrentUser() ? 'You' : subscriber.getNote();

    return (
      <ClickEventTrigger eventElementName="multiselect_user_row">
        <tr className={classes} onClick={rowIsEnabled ? this.handleClick : () => {}}>
          {adminConsoleAccess ? (
            <td className="PeopleTableRow-avatarCheckbox">
              <Checkbox checked={this.props.isSelected} className="PeopleTableRow-checkbox" />
            </td>
          ) : null}
          <td>
            <MediaObject gutter="small" center>
              <UserAvatar user={subscriber.toJSON()} size="medium" />
              <Text truncate>
                {subscriber.getFullName()}
                {note && (
                  <Text truncate italic color="graphite" size="xxxsmall">
                    {note}
                  </Text>
                )}
              </Text>
            </MediaObject>
          </td>
          {this.renderRoleCell(organizationAffiliation)}
          {group.isRoot() ? null : (
            <td>
              <Timestamp date={membership.getCreatedAt()} format="long" />
            </td>
          )}
          {this.props.canViewFamily && (
            <td>
              <GroupMemberFamily
                userId={membership.getUserId()}
                familySummary={this.props.familySummary}
                canAddChild={membership.hasPermission('can_add_child')}
                canAddParent={membership.hasPermission('can_add_parent')}
                addButtonOnClick={this.stopPropagation}
              />
            </td>
          )}
          <td className="PeopleTableRow-actions" onClick={this.stopPropagation}>
            {!this.props.hasSelection && (
              <Buttons align="right">
                <LayerTrigger
                  placement="top"
                  alignment="middle"
                  layer={
                    <Tooltip>
                      {canChatWithUser
                        ? 'Send a message'
                        : shortDescriptionForChatEligibilityState(
                            subscriber.get('chat_eligibility_state')
                          )}
                    </Tooltip>
                  }>
                  <span>
                    <StartChatButton
                      subscriber={subscriber}
                      disabled={!canChatWithUser}
                      data-test="chat-button">
                      <Button
                        aria-label={`Send a message to ${subscriber.getFullName()}`}
                        level="icon"
                        color={chatColor}
                        size="xsmall"
                        disabled={!canChatWithUser}>
                        <Glyph name="chat-add-bubble" size="large" solo />
                      </Button>
                    </StartChatButton>
                  </span>
                </LayerTrigger>
                {canPhone && (
                  <PhoneCallButton
                    calleeUuid={subscriber.getUuid()}
                    calleeName={subscriber.getName()}
                    inline
                    denialReason={subscriber.getCanPhoneDenialReason()}
                  />
                )}
                <UserActionsMenuButton
                  className="GroupMembersTabListItem-actionButton"
                  tooltip={adminConsoleAccess || canEditGroup ? undefined : 'More actions'}
                  tooltipPlacement="top"
                  level="text"
                  isAdminView={adminConsoleAccess || canEditGroup}
                  groupToAdmin={group}
                  buttonSize="xxsmall"
                  glyphSize="medium"
                  user={mapEntitiesToUserActionsMenuButtonUser(subscriber, membership)}
                  onUserUpdate={this.handleUserUpdate}
                  onUserRemoved={this.handleUserRemoved}
                  showViewProfile
                  showBlockMember={!!membership}
                  showCopyToClass={this.showCopyToClass()}
                  showChangeRole={
                    organizationAffiliation
                      ? organizationAffiliation.can_have_role_initialized ||
                        organizationAffiliation.can_have_role_changed
                      : false
                  }
                  showRemoveMember={membership.hasPermission('can_remove_from_group')}
                  showRemoveFromSchool={membership.hasPermission('can_remove_from_org')}
                  showGetTranscript={membership.hasPermission('can_request_message_history')}
                  showReportAsUser={!membership || !membership.hasPermission('can_admin')}
                  showReportAsAdmin={membership && membership.hasPermission('can_admin')}
                />
              </Buttons>
            )}
          </td>
        </tr>
      </ClickEventTrigger>
    );
  }
}

function mapStateToProps(state: any, props: Props) {
  const table = state.peopleTables.get(props.group.id);

  return {
    hasSelection: table.selectedMemberships.size > 0,
    isSelected: table.selectedMemberships.has(props.membership.id),
  };
}

function mapDispatchToProps(dispatch: Function) {
  // $FlowIssue
  return bindActionCreators(
    { removeMembers, loadMembership, selectMembership, unselectMembership },
    dispatch
  );
}

export { PeopleTableRow };
export default connect(mapStateToProps, mapDispatchToProps)(withRouter(PeopleTableRow));
